import React from 'react';
import { DOMAIN_CONFIG } from '../config';
import { Code2, ShieldCheck, Search, TrendingUp, Sparkles, CheckCircle2 } from 'lucide-react';

export const ValueProps: React.FC = () => {
  return (
    <section id="why-this-domain" className="py-20 md:py-28 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-900 border border-slate-700/80 text-emerald-400 text-xs font-mono mb-4">
            <Sparkles className="w-3.5 h-3.5" />
            <span>STRATEGIC VALUE PROPOSITION</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white tracking-tight">
            Why <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">{DOMAIN_CONFIG.domainName}</span> Commands Market Authority
          </h2>
          <p className="mt-4 text-base sm:text-lg text-slate-300">
            A premium domain is not an expense - it is permanent, appreciating digital real estate that anchors credibility and captures direct enterprise attention.
          </p>
        </div>

        {/* Feature Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {/* Card 1: .dev Prestige */}
          <div className="glass-panel glass-panel-hover rounded-2xl p-8 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 rounded-full blur-2xl group-hover:bg-emerald-500/20 transition-all"></div>
            <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 mb-6">
              <Code2 className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3">Dedicated .dev Developer TLD</h3>
            <p className="text-slate-300 leading-relaxed mb-4 text-sm sm:text-base">
              Engineered specifically for developers and technical innovators, <code className="text-emerald-400 bg-emerald-950/40 px-1.5 py-0.5 rounded font-mono">.dev</code> is the recognized global standard for software engineering tools, developer hubs, and modern technical platforms. Built-in HSTS ensures mandatory HTTPS security.
            </p>
            <ul className="space-y-2 text-xs sm:text-sm text-slate-400">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>Modern tech credibility that resonates with developers and CTOs</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>High memorability compared to lengthy hyphens or awkward alternatives</span>
              </li>
            </ul>
          </div>

          {/* Card 2: Enterprise Ecosystem */}
          <div className="glass-panel glass-panel-hover rounded-2xl p-8 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/10 rounded-full blur-2xl group-hover:bg-cyan-500/20 transition-all"></div>
            <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center text-cyan-400 mb-6">
              <TrendingUp className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3">Enterprise Cloud & IT Services Market</h3>
            <p className="text-slate-300 leading-relaxed mb-4 text-sm sm:text-base">
              Enterprise technology consulting, automated service management, and cloud architecture command massive global budgets. Organizations demand instant, high-availability service delivery.
            </p>
            <ul className="space-y-2 text-xs sm:text-sm text-slate-400">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-cyan-400 shrink-0" />
                <span>Action-oriented branding: "Service-Now" conveys immediate response</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-cyan-400 shrink-0" />
                <span>High total addressable market across cloud consulting and SaaS</span>
              </li>
            </ul>
          </div>

          {/* Card 3: Exact-Match Affinity & SEO */}
          <div className="glass-panel glass-panel-hover rounded-2xl p-8 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-32 h-32 bg-violet-500/10 rounded-full blur-2xl group-hover:bg-violet-500/20 transition-all"></div>
            <div className="w-12 h-12 rounded-xl bg-violet-500/10 border border-violet-500/30 flex items-center justify-center text-violet-400 mb-6">
              <Search className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3">Exact-Match Affinity & Direct Traffic</h3>
            <p className="text-slate-300 leading-relaxed mb-4 text-sm sm:text-base">
              Engineers and IT decision-makers seeking modern dev tools, microservices, or rapid consulting intuitively connect with <code className="text-violet-300 bg-violet-950/40 px-1.5 py-0.5 rounded font-mono">service-now.dev</code> as an authoritative address.
            </p>
            <ul className="space-y-2 text-xs sm:text-sm text-slate-400">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-violet-400 shrink-0" />
                <span>Organic search click-through rate advantage for developer topics</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-violet-400 shrink-0" />
                <span>Zero brand confusion - direct, unmistakable naming</span>
              </li>
            </ul>
          </div>

          {/* Card 4: Verified Transaction & Asset Safety */}
          <div className="glass-panel glass-panel-hover rounded-2xl p-8 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 rounded-full blur-2xl group-hover:bg-emerald-500/20 transition-all"></div>
            <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 mb-6">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3">Immediate Escrow & Clean Lineage</h3>
            <p className="text-slate-300 leading-relaxed mb-4 text-sm sm:text-base">
              The domain is held in good standing with a reputable registrar, free of liens or historical penalties, and immediately unlocked for transfer via Escrow.com or Sedo.
            </p>
            <ul className="space-y-2 text-xs sm:text-sm text-slate-400">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>Buyer funds protected until the domain is securely in your control</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>Same-day authorization code transfer upon escrow verification</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};
